/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import { InlineAlert } from "common/alerts";
import { LoginStyles } from "customStyles/index";
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useCookies from "react-cookie/cjs/useCookies";
import { redirect } from "react-router-dom";
import { useLoginStore } from "state/LoginStore";
import { urls } from "urls";
import { MFAPasswordReset } from "../../components/MFA-PasswordReset/MFAPasswordReset";
import { useAuth } from "../../hooks/useAuth";
import { useTableStore, useUserStore } from "../../state";
import { useMUITheme } from "../../theme/ThemeProvider";
import { errorTypes } from '../../utils/errorTypes';
import { defineAlertStyle } from "./components";
import ForgotPasswordButton from './components/ForgotPasswordButton'
import { fetchPreAuthToken } from "./lib"

export const Login = () => {
  const [{ userName, password }, setFields] = useState<any>({
    userName: "",
    password: "",
  });
  const [, setCookie] = useCookies();
  const { mode, saveTheme } = useMUITheme();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token] = useState("");
  const [user_name, setUserName] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [startMFAFlow, setStartMFAFlow] = useState(false);
  const [stateLogo, setStateLogo] = useState('')
  const [pwError, setPwError] = useState(false);
  const [pwErrorText, setPwErrorText] = useState('');
  const [btnPwErrorState, setBtnPwErrorState] = useState(false);
  const inputRef = useRef<any>(null);
  const [userNameError, setUserNameError] = useState(false);
  const [userNameErrorText, setUserNameErrorText] = useState('');
  const [btnUserErrorState, setBtnUserErrorState] = useState(false);
  const sessionExpired = sessionStorage.getItem("Session expired");
  const [preLoginTheme, setPreLoginTheme] = useState<string | null>(null);

  // Table Store:
  const alertTitle = useLoginStore((state) => state.alertTitle)
  const setAlertTitle = useLoginStore((state) => state.setAlertTitle)
  const alertDescription = useLoginStore((state) => state.alertDescription)
  const setAlertDescription = useLoginStore((state) => state.setAlertDescription)
  const severity = useLoginStore((state) => state.severity)
  const setSeverity = useLoginStore((state) => state.setSeverity)
  const openAlert = useLoginStore((state) => state.openAlert)
  const setOpenAlert = useLoginStore((state) => state.setOpenAlert)
  const setStateName = useLoginStore((state) => state.setStateName)

  const STATE = process.env.REACT_APP_CONFIG_STATE!;
  console.log('debug refactor config STATE declares', STATE)

  const [maintenanceSchedule, setMaintenanceSchedule] = useState<any>({ maintenanceActive: false })

  const userConfig = useUserStore(state => state.userConfig)
  const setConfig = useUserStore(state => state.setConfig)
  const { loggedIn } = userConfig

  const alertStyle = defineAlertStyle(severity, sessionExpired)
  const getMaintenanceSchedule = (config) => {
    const startDate = dayjs(config.startDate);
    const endDate = dayjs(config.endDate);
    const currentDate = dayjs();
    const isDuringMaintenance = currentDate.isAfter(startDate) && currentDate.isBefore(endDate);
    let maintenanceActive;

    if (startDate && endDate) {
      if (isDuringMaintenance) {
        maintenanceActive = true
        setSeverity("warning")
        setOpenAlert(true)
        setAlertTitle("Maintenance Scheduled.")
        setAlertDescription("This website is under maintenance, please check back later.")
      } else {
        maintenanceActive = false
      }
    }
    setMaintenanceSchedule({ startDate, endDate, maintenanceActive, currentDate })
  }

  const getConfig = useMemo(() => async () => {
    console.log('debug applicant details getConfig running STATE: ', STATE)
    const configUrl = urls.CONFIG;
    const controller = new AbortController();
    const { signal } = controller;
    const fetchUrl: string = process.env.NODE_ENV === 'production' ? `${configUrl}/config/file/public/config_${STATE}.json` : `../../config_${STATE}.json`; //Allow for config changes to be tested in development from local public folder

    try {
      const resp = await (await fetch(fetchUrl, {
        method: "GET",
        credentials: "include",
        signal
      })).json();

      const data = process.env.NODE_ENV === 'production' ? JSON.parse(resp.value) : resp
      console.log('config data: ', data)
      if (data?.logo) {
        let logo = data.logo.label;
        let stateName = data.logo.state
        setStateName(stateName)
        setStateLogo(logo)
        getMaintenanceSchedule(data?.scheduledMaintenance);
      }

      setConfig(data)
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    console.log('debug getConfig in login useEffect')
    getConfig()
  }, []);

  useEffect(() => {
    const currentTheme = localStorage.getItem("theme");
    setPreLoginTheme(currentTheme);
  }, []);

  useEffect(() => {
    inputRef.current!.focus({
      cursor: "start",
    });
  }, []);

  useEffect(() => {
    if (sessionExpired) {
      setSeverity("warning")
      setOpenAlert(true)
      setAlertTitle('Session Expired')
      setAlertDescription("Your session is expired. Please log back in.")
    }
  }, [sessionExpired]);

  useEffect(() => {
    const setPreAuthToken =
      async () => {
        try {
          const jsonResponse = await fetchPreAuthToken();
          const access_token = jsonResponse.access_token

          setCookie("token", `${access_token}`, {
            path: "/",
            expires: expDate,
            sameSite: "lax",
            domain: process.env.REACT_APP_DOMAIN_NAME!
          });
        } catch (err) {
          console.error(`Error getting pre auth token ${err}`);
        }
      }
    if (process.env.NODE_ENV === 'production') setPreAuthToken();
  }, [STATE]);

  const updateUserField = (e: any) => {
    const { name, value } = e.target;
    setUserNameError(true);
    setBtnUserErrorState(true);
    if (value.trim() === '') {
      setUserNameErrorText('Username is required.');
    } else if (value.includes(" ")) {
      setUserNameErrorText('Username can not contain blank spaces.');
    } else {
      setFields((currentValues: any) => ({ ...currentValues, [name]: value.trim() }));
      setErrorMessage(null);
      setUserNameError(false);
      setBtnUserErrorState(false);
      setPwErrorText('');
    }
  };

  const updatePwField = (e: any) => {
    const { name, value } = e.target;
    setPwError(true);
    setBtnPwErrorState(true);
    if (value.trim() === '') {
      setPwErrorText('Password is required.');
    } else if (value.includes(" ")) {
      setPwErrorText('Password can not contain blank spaces.');
    } else {
      setFields((currentValues: any) => ({ ...currentValues, [name]: value.trim() }));
      setErrorMessage(null);
      setPwError(false);
      setBtnPwErrorState(false);
      setPwErrorText('');
    }
  };

  const {
    login,
    mfa,
    userForgotPassword,
    forceResetPassword,
    userAcceptingTerms,
    handleResetPassword
  } = useAuth();

  const handleSubmit = useCallback(async (e: any) => {
    console.log(`debug login handleSubmit userName: ${userName} password: ${password} ran e: `, e);
    setAlertTitle("")
    setAlertDescription("")
    setSeverity("")
    setOpenAlert(false)
    e.preventDefault();
    setLoading(true);
    let res;
    res = await login?.({ userName, password });
    setLoading(false);
    if (res.username) {
      console.log("login success ", res);
      setOpenAlert(false)

      const currentTheme = localStorage.getItem("theme");
      const themeChangedPreLogin = preLoginTheme !== currentTheme;

      sessionStorage.clear();

      if (res.userConfig?.theme) {
        const themeToUse = themeChangedPreLogin && currentTheme ? currentTheme : res.userConfig.theme;
        useUserStore.getState().setUserConfig({
          ...res.userConfig,
          theme: themeToUse
        });

        if (themeChangedPreLogin && currentTheme) {
          localStorage.setItem("pendingThemeSave", currentTheme);
        }
      } else if (themeChangedPreLogin && currentTheme) {
        localStorage.setItem("pendingThemeSave", currentTheme);
      }
    } else {
      console.log("error: ", res);
      setSeverity("error")
      setOpenAlert(true)

      const errorMessage = res?.errorMessage;
      const errorCode = res?.errorCode;

      if (errorTypes[errorMessage]) {
        setAlertTitle(errorTypes[errorMessage].title);
        setAlertDescription(errorTypes[errorMessage].description);
      } else if (errorTypes[errorCode]) {
        setAlertTitle(errorTypes[errorCode].title);
        setAlertDescription(errorTypes[errorCode].description);
      } else {
        setAlertTitle(errorMessage || 'An unknown error occurred.');
        setAlertDescription('');
      }
    }
  }, [userName, password, saveTheme, preLoginTheme]);

  useEffect(() => {
    if (loggedIn) redirect("/app/transaction-summary");
  }, [loggedIn]);

  useEffect(() => {
    const shouldStartAuthFlow = mfa || userForgotPassword || forceResetPassword || userAcceptingTerms;

    if (shouldStartAuthFlow) {
      setOpenAlert(false);
      setSeverity("");
      setAlertTitle("");
      setAlertDescription("");
    }

    setStartMFAFlow(shouldStartAuthFlow);
  }, [userForgotPassword, forceResetPassword, mfa, userAcceptingTerms]);

  const resetPassword = async (
    finalPassword: string,
    code = null,
    userName: string,
    oldPass: string
  ) => {
    let res = await handleResetPassword?.(finalPassword, code, userName, oldPass);
    if (res) {
      setStartMFAFlow(false);
      setUserName(false);
      setSeverity("success")
      setOpenAlert(true)
      setAlertTitle("Password Updated!")
      setAlertDescription("Please Login using your new password.")
    }
  };

  const cancel = (e: boolean) => {
    setUserName(e)
    setStartMFAFlow(e);
    console.log("has been cancelled");
  };

  const cancelWithMessage = (e: boolean, severity: string, title: string, message: string) => {
    setUserName(e)
    setStartMFAFlow(e);
    setOpenAlert(true);
    setSeverity(severity);
    setAlertTitle(title);
    setAlertDescription(message);
    console.log("has been cancelled with a returning message");
  };

  const expDate = useMemo(() => {
    let date = new Date()
    date.setDate(date.getDate() + 1)
    return date
  }, []);

  useEffect(() => {
    console.log(
      `login errorMessage: ${errorMessage} startMFAFlow: ${startMFAFlow}`
    );
  }, [errorMessage, startMFAFlow, token]);

  const closeSessionExpired = () => {
    setOpenAlert(false);
    setSeverity("")
    setAlertTitle('')
    setAlertDescription("")
    sessionStorage.clear();
  }
  console.log('debug mode: ', mode)

  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  }

  function createRapbackSchemaFunction(tenantId: string) {
    const today = new Date();
    const oneDayLater = new Date(today.getTime() + (24 * 60 * 60 * 1000)); // Adding 1 day
    const subscriptionDate = formatDate(oneDayLater);
    const expirationDate = new Date(oneDayLater);
    expirationDate.setMonth(expirationDate.getMonth() + 38);
    const expirationDateString = formatDate(expirationDate);
    console.log('subscriptionDate', subscriptionDate, 'expirationDateString', expirationDateString);
  }

  createRapbackSchemaFunction('1234');

  return (
    <>
      {(
        !startMFAFlow ? (
          <>
            <CardHeader
              sx={{ width: '31.5rem' }}
              title='Sign In'
              data-testid="signin-card-header"
              titleTypographyProps={{ variant: 'h5', color: 'text.primary', mt: '0.5rem' }}
              subheader={`Enter your credentials to access the ${stateLogo} Portal.`}
              subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary', mt: '0.25rem' }} />
            <Box component="form">
              <CardContent sx={LoginStyles.mfaCardContentStyle}>
                {openAlert && severity && (
                  <InlineAlert
                    severity={severity}
                    title={alertTitle}
                    message={alertDescription}
                    dismissible={Boolean(sessionExpired)}
                    onClose={sessionExpired ? closeSessionExpired : undefined}
                    sx={alertStyle}
                  />
                )}
                <TextField
                  onChange={updateUserField}
                  onBlur={updateUserField}
                  variant={mode === 'dark' ? "filled" : "outlined"}
                  required
                  fullWidth
                  id="userName"
                  label="Username"
                  name="userName"
                  autoComplete="username"
                  aria-label="username"
                  data-testid="username-text-field"
                  error={userNameError}
                  helperText={userNameError ? userNameErrorText : ''}
                  autoFocus
                  InputLabelProps={{ sx: { color: userNameError ? 'red' : 'text.secondary' } }}
                  InputProps={{ sx: { color: 'text.primary' } }}
                  inputRef={inputRef}
                  disabled={maintenanceSchedule.maintenanceActive}
                />
                <TextField
                  onChange={updatePwField}
                  onBlur={updatePwField}
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  aria-label='password'
                  data-testid="password-text-field"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  error={pwError}
                  helperText={pwError ? pwErrorText : ''}
                  variant={mode === 'dark' ? "filled" : "outlined"}
                  InputLabelProps={{ style: { color: 'text.secondary' } }}
                  disabled={maintenanceSchedule.maintenanceActive}
                  InputProps={{
                    style: { color: 'text.primary' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Toggle Password Visibility">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={() => setShowPassword(!showPassword)}
                            style={{ color: '#808086' }}
                            data-testid={showPassword ? "visibility-on-icon" : "visibility-off-icon"}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </Tooltip>

                      </InputAdornment>
                    )
                  }}
                />
                <div tabIndex={0} style={{ width: '100%' }}>
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    disabled={(userName && password) && !btnUserErrorState && !btnPwErrorState && !maintenanceSchedule.maintenanceActive ? false : true}
                    color="secondary"
                    variant="contained"
                    type="submit"
                    tabIndex={2}
                    aria-disabled={(userName && password) ? false : true}
                    aria-live="assertive"
                    aria-label='Sign in'
                    size="large"
                    data-testid='signin-button'
                  >
                    {!loading ? 'SIGN IN' : <CircularProgress size='1.5rem' sx={{ marginLeft: '0.5rem', color: '#fff' }} />}
                  </Button>

                </div>
                <ForgotPasswordButton
                  maintenanceSchedule={maintenanceSchedule}
                  setUserName={setUserName}
                  setStartMFAFlow={setStartMFAFlow}
                  setOpenAlert={setOpenAlert}
                  setSeverity={setSeverity}
                  stateLogo={stateLogo}
                />

              </CardContent>
            </Box>
          </>
        ) : (
          <MFAPasswordReset
            cancel={cancel}
            cancelWithMessage={cancelWithMessage}
            resetPassword={resetPassword}
            userName={user_name}
            stateLogo={stateLogo}
          />
        )
      )}
    </>
  );
};
